import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { airports } from "../lib/airports"; 

export default function DetalhesVendaModal({ venda, isOpen, onClose }) {
  if (!venda) return null;

  const getAirportInfo = (iata) => {
    if (!iata) return 'N/A';
    const airport = airports.find(a => a.iata.toLowerCase() === iata.toLowerCase());
    if (airport) {
      return `${airport.city} - ${airport.name} (${airport.iata})`;
    }
    return iata; 
  };

  const formatMoney = (val) => `R$ ${Number(val || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  const formatDateTime = (str) => str ? new Date(str).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-';
  const formatDate = (str) => str ? new Date(str).toLocaleDateString('pt-BR') : '-';

  const InfoItem = ({ label, value, className = "" }) => (
    <div className={className}>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
      <p className="text-sm text-gray-900 font-medium mt-0.5">{value}</p>
    </div>
  );

  // Divide as categorias se houver mais de uma
  const categorias = venda.categoria ? venda.categoria.split(', ') : [];

  // Soma total do Aéreo (Custo e Venda) para exibir uma vez só
  const aereoTotalCusto = venda.trechos?.reduce((sum, t) => sum + (parseFloat(t.valor_custo) || 0), 0) || 0;
  const aereoTotalVenda = venda.trechos?.reduce((sum, t) => sum + (parseFloat(t.valor_venda) || 0), 0) || 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[750px]">
        <DialogHeader>
          <DialogTitle>Detalhes da Venda</DialogTitle>
          <DialogDescription>
            Informações completas da venda realizada em {formatDateTime(venda.data_venda)}.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-6 py-4 max-h-[75vh] overflow-y-auto pr-4">
          
          {/* Cabeçalho Resumido */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 border rounded-lg">
            <div className="col-span-2 md:col-span-4">
              <p className="text-xs font-medium text-gray-500 uppercase">Categorias</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {categorias.map((cat, i) => (
                   <Badge key={i} variant="secondary" className="bg-blue-100 text-blue-800 hover:bg-blue-100">{cat}</Badge>
                ))}
              </div>
            </div>
            <InfoItem className="col-span-2" label="Clientes" value={(venda.clientes_nomes || []).join(', ')} />
            <InfoItem label="Localizador Master" value={venda.localizador || '-'} />
            <InfoItem label="Fornecedor Master" value={venda.fornecedor_nome || '-'} />
            <InfoItem label="Vendedor" value={venda.vendedor_nome || '-'} />
            <InfoItem label="Pagamento" value={venda.forma_pagamento || '-'} />
          </div>

          {/* Financeiro Master */}
          <div className="grid grid-cols-3 gap-4 p-4 border rounded-lg bg-gray-50">
            <InfoItem label="Custo Total (Fornecedor)" value={<span className="text-orange-600 text-lg">{formatMoney(venda.custo)}</span>} />
            <InfoItem label="Venda Total (Cliente)" value={<span className="text-blue-600 text-lg">{formatMoney(venda.valor_total)}</span>} />
            <div className="text-right">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Lucro Bruto</p>
                <p className={`text-xl font-bold mt-0.5 ${venda.lucro >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatMoney(venda.lucro)}
                </p>
            </div>
          </div>
          
          {/* ================= SEÇÕES DINÂMICAS ================= */}

          {venda.trechos?.length > 0 && (
            <div>
              <div className="flex justify-between items-center mb-3">
                 <h4 className="font-semibold text-blue-800">✈️ Aéreo</h4>
                 <div className="flex gap-4 text-xs font-semibold bg-blue-100 px-3 py-1.5 rounded-full border border-blue-200">
                    <span className="text-orange-600">Custo Total Aéreo: {formatMoney(aereoTotalCusto)}</span>
                    <span className="text-blue-700">Venda Total Aéreo: {formatMoney(aereoTotalVenda)}</span>
                 </div>
              </div>
              <div className="space-y-3">
                {venda.trechos.map((t, i) => (
                  <div key={i} className="p-4 border border-blue-100 rounded-md bg-blue-50/30">
                    <div className="flex justify-between items-center mb-3 pb-2 border-b border-blue-100">
                      <span className="font-bold text-sm text-blue-900">Trecho {i + 1} ({t.tipo})</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <InfoItem label="Origem" value={getAirportInfo(t.origem)} className="col-span-2" />
                      <InfoItem label="Destino" value={getAirportInfo(t.destino)} className="col-span-2" />
                      <InfoItem label="Cia Aérea" value={t.companhia_nome} />
                      <InfoItem label="Partida" value={formatDateTime(t.data_partida)} />
                      <InfoItem label="Chegada" value={formatDateTime(t.data_chegada)} />
                      <InfoItem label="Localizador" value={t.localizador || '-'} />
                    </div>
                  </div>
                ))}

                {/* Ancilares do Aéreo (Agora "Extras do Voo") */}
                {(venda.bagagens?.length > 0 || venda.reservas?.length > 0 || venda.pets?.length > 0) && (
                  <div className="p-3 bg-blue-50 border border-blue-100 rounded-md">
                     <p className="text-xs font-bold text-blue-900 uppercase mb-2">Extras do Voo</p>
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {venda.bagagens?.map((b, i) => (
                           <p key={`b-${i}`} className="text-sm text-gray-700">• Bagagem: Custo {formatMoney(b.valor_custo)} | Venda {formatMoney(b.valor_venda)}</p>
                        ))}
                        {venda.reservas?.map((r, i) => (
                           <p key={`r-${i}`} className="text-sm text-gray-700">• Assento: Custo {formatMoney(r.valor_custo)} | Venda {formatMoney(r.valor_venda)}</p>
                        ))}
                        {venda.pets?.map((p, i) => (
                           <p key={`p-${i}`} className="text-sm text-gray-700">• Pet: Custo {formatMoney(p.valor_custo)} | Venda {formatMoney(p.valor_venda)}</p>
                        ))}
                     </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {venda.hospedagens?.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3 text-yellow-800">🏨 Hospedagem</h4>
              <div className="space-y-3">
                {venda.hospedagens.map((h, i) => (
                  <div key={i} className="p-4 border border-yellow-200 rounded-md bg-yellow-50/30">
                    <div className="flex justify-between items-center mb-3 pb-2 border-b border-yellow-200">
                      <span className="font-bold text-sm text-yellow-900">Hotel {i + 1}</span>
                      <div className="flex gap-4 text-xs font-semibold">
                         <span className="text-orange-600">Custo: {formatMoney(h.valor_custo)}</span>
                         <span className="text-blue-600">Venda: {formatMoney(h.valor_venda)}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <InfoItem label="Hotel" value={h.hotel} className="col-span-2" />
                      <InfoItem label="Quarto" value={h.quarto || '-'} className="col-span-2" />
                      <InfoItem label="Check-in" value={formatDate(h.checkin)} />
                      <InfoItem label="Check-out" value={formatDate(h.checkout)} />
                      <InfoItem label="Fornecedor" value={h.fornecedor_nome || '-'} />
                      <InfoItem label="Reserva" value={h.localizador || '-'} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {venda.automoveis?.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3 text-slate-800">🚗 Automóvel</h4>
              <div className="space-y-3">
                {venda.automoveis.map((a, i) => (
                  <div key={i} className="p-4 border border-slate-200 rounded-md bg-slate-50">
                    <div className="flex justify-between items-center mb-3 pb-2 border-b border-slate-200">
                      <span className="font-bold text-sm text-slate-900">Veículo {i + 1}</span>
                      <div className="flex gap-4 text-xs font-semibold">
                         <span className="text-orange-600">Custo: {formatMoney(a.valor_custo)}</span>
                         <span className="text-blue-600">Venda: {formatMoney(a.valor_venda)}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <InfoItem label="Locadora" value={a.locadora} className="col-span-2" />
                      <InfoItem label="Modelo" value={a.modelo || '-'} className="col-span-2" />
                      <InfoItem label="Retirada" value={formatDateTime(a.retirada)} />
                      <InfoItem label="Devolução" value={formatDateTime(a.devolucao)} />
                      <InfoItem label="Broker" value={a.fornecedor_nome || '-'} />
                      <InfoItem label="Voucher" value={a.localizador || '-'} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {venda.transfers?.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3 text-teal-800">🚐 Transfer</h4>
              <div className="space-y-3">
                {venda.transfers.map((t, i) => (
                  <div key={i} className="p-4 border border-teal-200 rounded-md bg-teal-50/50">
                    <div className="flex justify-between items-center mb-3 pb-2 border-b border-teal-200">
                      <span className="font-bold text-sm text-teal-900">Transfer {i + 1}</span>
                      <div className="flex gap-4 text-xs font-semibold">
                         <span className="text-orange-600">Custo: {formatMoney(t.valor_custo)}</span>
                         <span className="text-blue-600">Venda: {formatMoney(t.valor_venda)}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <InfoItem label="Trajeto" value={t.trajeto} className="col-span-2" />
                      <InfoItem label="Data/Hora" value={formatDateTime(t.data)} />
                      <InfoItem label="Fornecedor" value={t.fornecedor_nome || '-'} />
                      <InfoItem label="Voucher" value={t.localizador || '-'} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {venda.passeios?.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3 text-orange-800">🗺️ Passeio / Ingresso</h4>
              <div className="space-y-3">
                {venda.passeios.map((p, i) => (
                  <div key={i} className="p-4 border border-orange-200 rounded-md bg-orange-50/50">
                    <div className="flex justify-between items-center mb-3 pb-2 border-b border-orange-200">
                      <span className="font-bold text-sm text-orange-900">Atividade {i + 1}</span>
                      <div className="flex gap-4 text-xs font-semibold">
                         <span className="text-orange-600">Custo: {formatMoney(p.valor_custo)}</span>
                         <span className="text-blue-600">Venda: {formatMoney(p.valor_venda)}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <InfoItem label="Descrição" value={p.descricao} className="col-span-2" />
                      <InfoItem label="Data/Hora" value={formatDateTime(p.data)} />
                      <InfoItem label="Fornecedor" value={p.fornecedor_nome || '-'} />
                      <InfoItem label="Ticket" value={p.localizador || '-'} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {venda.seguros?.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3 text-emerald-800">🛡️ Seguro Viagem</h4>
              <div className="space-y-3">
                {venda.seguros.map((s, i) => (
                  <div key={i} className="p-4 border border-emerald-200 rounded-md bg-emerald-50/30">
                    <div className="flex justify-between items-center mb-3 pb-2 border-b border-emerald-200">
                      <span className="font-bold text-sm text-emerald-900">Seguro {i + 1}</span>
                      <div className="flex gap-4 text-xs font-semibold">
                         <span className="text-orange-600">Custo: {formatMoney(s.valor_custo)}</span>
                         <span className="text-blue-600">Venda: {formatMoney(s.valor_venda)}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <InfoItem label="Seguradora" value={s.seguradora} className="col-span-2" />
                      <InfoItem label="Plano" value={s.plano} className="col-span-2" />
                      <InfoItem label="Início" value={formatDate(s.data_inicio)} />
                      <InfoItem label="Fim" value={formatDate(s.data_fim)} />
                      <InfoItem label="Fornecedor" value={s.fornecedor_nome || '-'} />
                      <InfoItem label="Apólice" value={s.localizador || '-'} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {venda.outros?.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3 text-gray-800">📌 Outros Serviços / Taxas DU</h4>
              <div className="space-y-3">
                {venda.outros.map((o, i) => (
                  <div key={i} className="p-3 border border-gray-300 rounded-md bg-gray-100 flex justify-between items-center">
                    <p className="text-sm font-medium text-gray-900">{o.descricao}</p>
                    <div className="flex gap-4 text-xs font-semibold">
                        <span className="text-orange-600">Custo: {formatMoney(o.valor_custo)}</span>
                        <span className="text-blue-600">Venda: {formatMoney(o.valor_venda)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Descontos */}
          {venda.descontos?.length > 0 && (
             <div>
                <h4 className="font-semibold mb-2 text-red-800">Descontos Globais</h4>
                <div className="space-y-2">
                   {venda.descontos.map((d, i) => (
                     <div key={i} className="p-3 bg-red-50 border border-red-100 rounded-md flex justify-between items-center">
                         <span className="text-sm font-medium text-red-900">Desconto {i + 1}</span>
                         <div className="flex gap-4 text-xs font-semibold">
                             <span className="text-red-600">Redução Custo: {formatMoney(d.valor_custo)}</span>
                             <span className="text-red-600">Redução Venda: {formatMoney(d.valor_venda)}</span>
                         </div>
                     </div>
                   ))}
                </div>
             </div>
          )}

           {/* Observações */}
           {venda.observacao && (
             <div>
              <h4 className="font-semibold mb-2 text-gray-800">Observações</h4>
              <div className="p-4 border rounded-md bg-white min-h-[80px]">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{venda.observacao}</p>
              </div>
            </div>
           )}
        </div>
        
        <DialogFooter>
          <Button onClick={onClose} variant="outline">Fechar Visualização</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}